import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Bell, CalendarDays, Heart, Loader2, MapPin, Search, Store } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import Navbar from "@/components/Navbar";
import SiteAnnouncementBanner from "@/components/SiteAnnouncementBanner";
import { BusinessDetailModal } from "@/pages/DirectoryPage";
import { MarketDetailModal } from "@/pages/MarketDirectoryPage";
import { AnnouncementsPanel } from "@/components/AnnouncementsPanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const DEFAULT_BG_IMAGE = "https://customer-assets.emergentagent.com/job_biz-directory-27/artifacts/miycx0rf_614shoplocal-background.png";

const getLogoUrl = (logo) => {
  if (!logo) return null;
  if (logo.startsWith("http") || logo.startsWith("data:") || logo.startsWith("blob:")) return logo;
  const baseUrl = BACKEND_URL || window.location.origin;
  try {
    return new URL(logo, baseUrl).toString();
  } catch {
    return logo.startsWith("/") ? `${baseUrl.replace(/\/$/, "")}${logo}` : logo;
  }
};

const getBusinessSubcategories = (business) => {
  const categories = Array.isArray(business?.business_categories) ? business.business_categories : [];
  const visible = categories.filter((category) => category !== "Other");
  const customCategory = business?.business_category_other?.trim();
  if (customCategory) visible.push(customCategory);
  return Array.from(new Set(visible));
};

const formatMarketSeason = (market) => {
  if (market?.season_year_round) return "Year-round";
  const fmt = (value) => {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };
  const start = fmt(market?.season_start);
  const end = fmt(market?.season_end);
  if (start && end) return `${start} - ${end}`;
  return start || end || "";
};

const itemMatchesSearch = (item, type, term) => {
  const query = term.trim().toLowerCase();
  if (!query) return true;
  const fields = type === "business"
    ? [
        item.business_name,
        item.business_description,
        item.business_type,
        ...(item.business_categories || []),
        item.business_category_other,
        item.location,
        ...(item.business_locations || []),
      ]
    : [
        item.market_name,
        item.description,
        item.location,
        item.address,
        ...(item.associated_businesses || []).map((business) => business.business_name),
      ];
  return fields.filter(Boolean).some((value) => String(value).toLowerCase().includes(query));
};

const ShopperProfilePage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const token = user?.token;
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [selectedMarket, setSelectedMarket] = useState(null);
  const [announcementUnreadCount, setAnnouncementUnreadCount] = useState(0);
  const [dashboardTab, setDashboardTab] = useState("businesses");
  const [businessSearch, setBusinessSearch] = useState("");
  const [marketSearch, setMarketSearch] = useState("");

  const savedBusinesses = useMemo(() => profile?.saved_businesses || [], [profile?.saved_businesses]);
  const savedMarkets = useMemo(() => profile?.saved_markets || [], [profile?.saved_markets]);
  const filteredBusinesses = useMemo(
    () => savedBusinesses.filter((business) => itemMatchesSearch(business, "business", businessSearch)),
    [savedBusinesses, businessSearch]
  );
  const filteredMarkets = useMemo(
    () => savedMarkets.filter((market) => itemMatchesSearch(market, "market", marketSearch)),
    [savedMarkets, marketSearch]
  );

  const fetchProfile = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/shopper`, { headers: { Authorization: `Bearer ${token}` } });
      setProfile(response.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        logout();
        navigate("/login");
      } else {
        toast.error("Failed to load shopper profile");
      }
    } finally {
      setLoading(false);
    }
  }, [token, logout, navigate]);

  useEffect(() => {
    if (!token) {
      navigate("/login");
      return;
    }
    fetchProfile();
  }, [token, navigate, fetchProfile]);

  const handleFollow = async (type, id) => {
    try {
      const response = await axios.post(`${API}/shopper/follow/${type}/${id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setProfile((current) => {
        if (!current) return current;
        const idsKey = type === "business" ? "saved_business_ids" : "saved_market_ids";
        const listKey = type === "business" ? "saved_businesses" : "saved_markets";
        const nextIds = response.data[idsKey] || [];
        return {
          ...current,
          [idsKey]: nextIds,
          [listKey]: (current[listKey] || []).filter((item) => nextIds.includes(item.id)),
        };
      });
      if (type === "business") {
        setSelectedBusiness((current) => (response.data.following ? current : null));
      } else {
        setSelectedMarket((current) => (response.data.following ? current : null));
      }
      toast.success(response.data.following ? "Saved." : "Removed from your profile.");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update saved listing");
    }
  };

  if (loading) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <SiteAnnouncementBanner />

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <p className="flex items-center gap-2 text-[#42D78A] text-sm tracking-[0.2em] uppercase mb-3 font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Shopper Profile
            </p>
            <h1 className="text-3xl md:text-4xl font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {profile?.name ? `${profile.name}'s favorites` : "Your favorites"}
            </h1>
            <p className="text-gray-500 mt-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>{profile?.email}</p>
          </div>
          <div className="flex gap-3">
            <Link to="/directory"><Button variant="outline" className="rounded-full" data-testid="shopper-business-directory-btn">Browse Businesses</Button></Link>
            <Link to="/markets"><Button className="rounded-full bg-[#42D78A] hover:bg-[#3bc27c] text-[#1a1a1a] font-bold" data-testid="shopper-market-directory-btn">Browse Markets</Button></Link>
          </div>
        </div>

        <Tabs value={dashboardTab} onValueChange={setDashboardTab} className="space-y-6">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <TabsList className="h-auto flex-wrap justify-start rounded-full bg-gray-100 p-1">
              <TabsTrigger value="businesses" className="rounded-full px-5 py-2 font-bold data-[state=active]:bg-[#1a1a1a] data-[state=active]:text-white">
                Businesses
                <span className="ml-2 rounded-full bg-white px-2 py-0.5 text-xs text-[#1a1a1a]">{savedBusinesses.length}</span>
              </TabsTrigger>
              <TabsTrigger value="markets" className="rounded-full px-5 py-2 font-bold data-[state=active]:bg-[#1a1a1a] data-[state=active]:text-white">
                Markets
                <span className="ml-2 rounded-full bg-white px-2 py-0.5 text-xs text-[#1a1a1a]">{savedMarkets.length}</span>
              </TabsTrigger>
            </TabsList>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDashboardTab("announcements")}
              className={`h-11 w-fit rounded-full px-5 font-bold shadow-sm ${dashboardTab === "announcements" ? "border-[#1a1a1a] bg-[#1a1a1a] text-white hover:bg-[#1a1a1a] hover:text-white" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}
              data-testid="shopper-announcements-tab-button"
            >
              <Bell className="mr-2 h-4 w-4" /> Announcements
              {announcementUnreadCount > 0 && (
                <span className="ml-2 rounded-full bg-[#42D78A] px-2 py-0.5 text-xs font-black text-[#1a1a1a]">{announcementUnreadCount}</span>
              )}
            </Button>
          </div>

          <TabsContent value="businesses" className="mt-0">
            <SavedSection
              title="Saved Businesses"
              emptyText={businessSearch ? "No saved businesses match your search." : "No saved businesses yet."}
              items={filteredBusinesses}
              totalCount={savedBusinesses.length}
              type="business"
              searchValue={businessSearch}
              onSearchChange={setBusinessSearch}
              searchPlaceholder="Search saved businesses..."
              onOpen={setSelectedBusiness}
              onFollow={(id) => handleFollow("business", id)}
            />
          </TabsContent>

          <TabsContent value="markets" className="mt-0">
            <SavedSection
              title="Followed Markets"
              emptyText={marketSearch ? "No followed markets match your search." : "No followed markets yet."}
              items={filteredMarkets}
              totalCount={savedMarkets.length}
              type="market"
              searchValue={marketSearch}
              onSearchChange={setMarketSearch}
              searchPlaceholder="Search followed markets..."
              onOpen={setSelectedMarket}
              onFollow={(id) => handleFollow("market", id)}
            />
          </TabsContent>

          <TabsContent value="announcements" className="mt-0">
            <AnnouncementsPanel token={token} onUnreadCountChange={setAnnouncementUnreadCount} testId="shopper-announcements-panel" />
          </TabsContent>
        </Tabs>
      </main>

      <BusinessDetailModal
        business={selectedBusiness}
        open={!!selectedBusiness}
        onClose={() => setSelectedBusiness(null)}
        isSaved={selectedBusiness ? (profile?.saved_business_ids || []).includes(selectedBusiness.id) : false}
        onFollow={(id) => handleFollow("business", id)}
      />
      <MarketDetailModal
        market={selectedMarket}
        open={!!selectedMarket}
        onClose={() => setSelectedMarket(null)}
        isSaved={selectedMarket ? (profile?.saved_market_ids || []).includes(selectedMarket.id) : false}
        onFollow={(id) => handleFollow("market", id)}
      />
    </div>
  );
};

const SavedSection = ({
  title,
  emptyText,
  items,
  totalCount,
  type,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  onOpen,
  onFollow,
}) => (
  <section className="mb-12">
    <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
      <div className="flex items-center gap-3">
        <Heart className="w-5 h-5 text-[#42D78A]" />
        <h2 className="text-xl font-bold text-[#1a1a1a]" style={{ fontFamily: "'Outfit', sans-serif" }}>{title}</h2>
        <Badge className="bg-gray-100 text-gray-600 rounded-full">{totalCount}</Badge>
      </div>
      <div className="relative w-full md:max-w-sm">
        <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        <Input
          value={searchValue}
          onChange={(event) => onSearchChange(event.target.value)}
          placeholder={searchPlaceholder}
          className="h-11 rounded-full border-gray-200 pl-11 shadow-sm"
          data-testid={`shopper-${type}-search-input`}
        />
      </div>
    </div>
    {items.length === 0 ? (
      <div className="border border-dashed border-gray-200 rounded-2xl p-8 text-center text-gray-500" style={{ fontFamily: "'Montserrat', sans-serif" }}>{emptyText}</div>
    ) : (
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((item) => <SavedCard key={item.id} item={item} type={type} onOpen={onOpen} onFollow={onFollow} />)}
      </div>
    )}
  </section>
);

const SavedCard = ({ item, type, onOpen, onFollow }) => {
  const [logoFailed, setLogoFailed] = useState(false);
  const name = type === "business" ? item.business_name : item.market_name;
  const description = type === "business" ? item.business_description : item.description;
  const location = type === "business" ? item.location : item.location || item.city || item.area;
  const logoUrl = getLogoUrl(item.logo);
  const hasLogo = logoUrl && !logoFailed;
  const subcategories = type === "business" ? getBusinessSubcategories(item) : [];
  const season = type === "market" ? formatMarketSeason(item) : "";

  return (
    <div
      onClick={() => onOpen(item)}
      className="group cursor-pointer overflow-hidden rounded-2xl border border-gray-100 bg-white transition-all duration-300 hover:shadow-xl"
      data-testid={`shopper-saved-${type}-${item.id}`}
    >
      <div className="relative">
        <div className="relative flex items-center justify-center" style={{ width: "100%", height: 0, paddingBottom: "64.5%", backgroundImage: `url(${DEFAULT_BG_IMAGE})`, backgroundSize: "cover", backgroundPosition: "center" }}>
          {hasLogo ? (
            <div className="absolute inset-0 flex items-center justify-center bg-white p-6">
              <img
                src={logoUrl}
                alt={`${name || "Saved listing"} logo`}
                onError={() => setLogoFailed(true)}
                className="max-h-full max-w-full object-contain transition-transform duration-300 group-hover:scale-105"
                style={{ maxHeight: "80%", maxWidth: "80%" }}
              />
            </div>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center p-4">
              <span className="text-center text-xl font-bold text-slate-800" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {name || "Saved Listing"}
              </span>
            </div>
          )}
        </div>
      </div>
      <div className="p-5">
        <div className="mb-1 flex items-start justify-between gap-3">
          <h3 className="font-bold text-base text-[#1a1a1a] transition-colors group-hover:text-[#42D78A]" style={{ fontFamily: "'Outfit', sans-serif" }}>{name || "Unnamed Listing"}</h3>
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onFollow(item.id);
            }}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#42D78A] bg-[#42D78A] text-[#1a1a1a] transition-colors hover:bg-[#3bc27c]"
            aria-label={type === "business" ? "Remove saved business" : "Unfollow market"}
            data-testid={`shopper-remove-${type}-${item.id}`}
          >
            <Heart className="h-4 w-4 fill-current" />
          </button>
        </div>
        {description && (
          <p className="mb-3 line-clamp-2 text-sm text-gray-500" style={{ fontFamily: "'Montserrat', sans-serif" }}>{description}</p>
        )}
        <div className="space-y-1">
          {type === "business" && item.business_type && (
            <p className="text-xs font-medium text-gray-500" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {[item.business_type, ...subcategories].filter(Boolean).join(" · ")}
            </p>
          )}
          {type === "market" && season && (
            <p className="flex items-center gap-1 text-xs font-medium text-gray-500" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              <CalendarDays className="h-3 w-3" />{season}
            </p>
          )}
          {location && (
            <p className="flex items-center gap-1 text-xs font-medium text-gray-500" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              <MapPin className="h-3 w-3" />{location}
            </p>
          )}
          {type === "market" && item.associated_businesses?.length > 0 && (
            <p className="flex items-center gap-1 text-xs font-medium text-gray-500" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              <Store className="h-3 w-3" />{item.associated_businesses.length} market vendor{item.associated_businesses.length === 1 ? "" : "s"}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShopperProfilePage;
